import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getEducationalAdministrations, getGrades, getSchools } from '@/api/lookups.api';
import { convertLead } from '@/api/leads.api';
import { ApiError, type ApiErrorDetail } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { FormField } from '@/components/form/FormField';
import { LookupSelect } from '@/components/form/LookupSelect';
import { Stepper } from '@/components/form/Stepper';
import { showErrorToast } from '@/lib/toast';
import { genderOptions, guardianRelationshipOptions } from '@/lib/enumOptions';
import type {
  Gender,
  GuardianRelationship,
  LeadResponse,
  LookupItem,
  StudentResponse,
} from '@/types/shared';
import './ConvertLeadDialog.css';

interface FormState {
  nameAr: string;
  nameEn: string;
  dateOfBirth: string;
  gender: Gender | '';
  nationalId: string;
  gradeId: number | '';
  agreedSchoolId: number | '';
  educationalAdministrationId: number | '';
  address: string;
  guardianNameAr: string;
  guardianNameEn: string;
  guardianRelationship: GuardianRelationship | '';
  guardianPhone: string;
  guardianEmail: string;
  installmentCount: number | '';
  totalAgreedFees: number | '';
}

type FieldErrors = Partial<Record<keyof FormState, string>>;

function initialFormFor(lead: LeadResponse): FormState {
  return {
    nameAr: lead.fullName,
    nameEn: lead.fullName,
    dateOfBirth: '',
    gender: '',
    nationalId: '',
    gradeId: lead.gradeInterested?.id ?? '',
    agreedSchoolId: lead.schoolInterested?.id ?? '',
    educationalAdministrationId: '',
    address: '',
    guardianNameAr: '',
    guardianNameEn: '',
    guardianRelationship: '',
    guardianPhone: lead.phone,
    guardianEmail: lead.email ?? '',
    installmentCount: '',
    totalAgreedFees: '',
  };
}

interface ConvertLeadDialogProps {
  lead: LeadResponse;
  onClose: () => void;
  onConverted: (student: StudentResponse) => void;
}

/**
 * Convert modal — maps an `Attended` lead into a brand-new Student Code (`POST
 * /leads/{id}/convert`), per 03-rbac-and-lifecycle.md §3. The lead only carries a handful of
 * prospect fields (name/phone/grade/school interest), so this collects the rest of
 * `RegisterStudentDto`'s required Admissions/guardian fields the same way `StudentFormPage` does,
 * pre-seeded from what the lead already has. Reuses the `.fa-dialog*` scaffold/focus-trap from
 * `ConfirmDialog`, widened via `.fa-convert-dialog`, since the confirm action here needs a full
 * form rather than a single "are you sure" click.
 */
export function ConvertLeadDialog({ lead, onClose, onConverted }: ConvertLeadDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const [form, setForm] = useState<FormState>(() => initialFormFor(lead));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [grades, setGrades] = useState<LookupItem[]>([]);
  const [schools, setSchools] = useState<LookupItem[]>([]);
  const [eduAdmins, setEduAdmins] = useState<LookupItem[]>([]);
  const [loadingLookups, setLoadingLookups] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getGrades({ activeOnly: false }),
      getSchools({ activeOnly: false }),
      getEducationalAdministrations({ activeOnly: false }),
    ])
      .then(([gradeItems, schoolItems, eduAdminItems]) => {
        if (cancelled) return;
        setGrades(gradeItems);
        setSchools(schoolItems);
        setEduAdmins(eduAdminItems);
      })
      .catch(() => {
        if (!cancelled) setBanner(t('leads.convert.lookupLoadError'));
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const firstField = dialogRef.current?.querySelector<HTMLElement>('input, select, textarea');
    firstField?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!form.nameAr.trim()) errors.nameAr = t('leads.convert.errors.nameArRequired');
    if (!form.nameEn.trim()) errors.nameEn = t('leads.convert.errors.nameEnRequired');
    if (!form.dateOfBirth) errors.dateOfBirth = t('leads.convert.errors.dateOfBirthRequired');
    if (!form.gender) errors.gender = t('leads.convert.errors.genderRequired');
    if (form.gradeId === '') errors.gradeId = t('leads.convert.errors.gradeRequired');
    if (form.agreedSchoolId === '')
      errors.agreedSchoolId = t('leads.convert.errors.agreedSchoolRequired');
    if (form.educationalAdministrationId === '') {
      errors.educationalAdministrationId = t(
        'leads.convert.errors.educationalAdministrationRequired',
      );
    }
    if (!form.guardianRelationship) {
      errors.guardianRelationship = t('leads.convert.errors.guardianRelationshipRequired');
    }
    if (!form.guardianPhone.trim())
      errors.guardianPhone = t('leads.convert.errors.guardianPhoneRequired');
    if (
      form.installmentCount !== '' &&
      (!Number.isInteger(form.installmentCount) || form.installmentCount < 1)
    ) {
      errors.installmentCount = t('leads.convert.errors.installmentCountInvalid');
    }
    if (
      form.totalAgreedFees !== '' &&
      (Number.isNaN(form.totalAgreedFees) || form.totalAgreedFees < 0)
    ) {
      errors.totalAgreedFees = t('leads.convert.errors.totalAgreedFeesInvalid');
    }
    return errors;
  }

  function applyServerFieldErrors(details: ApiErrorDetail[]) {
    const errors: FieldErrors = {};
    for (const detail of details) {
      if (detail.field && detail.field in form) {
        errors[detail.field as keyof FormState] = detail.message;
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errors }));
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBanner(null);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      document.getElementById(`convert-${firstKey}`)?.focus();
      return;
    }

    setSubmitting(true);
    try {
      const student = await convertLead(lead.leadId, {
        nameAr: form.nameAr.trim(),
        nameEn: form.nameEn.trim(),
        dateOfBirth: form.dateOfBirth,
        gender: form.gender as Gender,
        nationalId: form.nationalId.trim() || undefined,
        gradeId: form.gradeId as number,
        agreedSchoolId: form.agreedSchoolId as number,
        educationalAdministrationId: form.educationalAdministrationId as number,
        address: form.address.trim() || undefined,
        guardianNameAr: form.guardianNameAr.trim() || undefined,
        guardianNameEn: form.guardianNameEn.trim() || undefined,
        guardianRelationship: form.guardianRelationship as GuardianRelationship,
        guardianPhone: form.guardianPhone.trim(),
        guardianEmail: form.guardianEmail.trim() || undefined,
        installmentCount: form.installmentCount === '' ? undefined : form.installmentCount,
        totalAgreedFees: form.totalAgreedFees === '' ? undefined : form.totalAgreedFees,
      });
      onConverted(student);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'NETWORK_ERROR') {
        const message = t('leads.convert.networkError');
        setBanner(message);
        showErrorToast(message);
      } else if (apiError.code === 'LEAD_NOT_READY_FOR_CONVERSION') {
        setBanner(t('leads.convert.notReadyError'));
      } else if (apiError.code === 'LEAD_ALREADY_CONVERTED') {
        setBanner(t('leads.convert.alreadyConvertedError'));
      } else {
        setBanner(apiError.message || t('leads.convert.serverError'));
        if (apiError.details.length > 0) applyServerFieldErrors(apiError.details);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const genders = genderOptions(t);
  const relationships = guardianRelationshipOptions(t);

  return (
    <div className="fa-dialog-overlay">
      <div
        className="fa-dialog fa-convert-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fa-convert-dialog-title"
        ref={dialogRef}
      >
        <h2 id="fa-convert-dialog-title" className="fa-dialog__title">
          {t('leads.convert.title')}
        </h2>
        <p className="fa-convert-dialog__intro">{t('leads.convert.description')}</p>

        {banner && (
          <Banner tone="danger" assertive>
            {banner}
          </Banner>
        )}

        {loadingLookups ? (
          <p className="fa-form-loading">{t('common.loading')}</p>
        ) : (
          <form noValidate onSubmit={handleSubmit} className="fa-convert-dialog__form">
            <div className="fa-convert-dialog__body">
              <h3 className="fa-convert-dialog__section-title">
                {t('leads.convert.studentSection')}
              </h3>
              <div className="fa-field-row">
                <FormField
                  id="convert-nameAr"
                  label={t('leads.convert.nameAr')}
                  required
                  error={fieldErrors.nameAr}
                >
                  <input
                    type="text"
                    value={form.nameAr}
                    disabled={submitting}
                    onChange={(e) => setField('nameAr', e.target.value)}
                  />
                </FormField>
                <FormField
                  id="convert-nameEn"
                  label={t('leads.convert.nameEn')}
                  required
                  error={fieldErrors.nameEn}
                >
                  <input
                    type="text"
                    value={form.nameEn}
                    disabled={submitting}
                    onChange={(e) => setField('nameEn', e.target.value)}
                  />
                </FormField>
              </div>

              <div className="fa-field-row">
                <FormField
                  id="convert-dateOfBirth"
                  label={t('leads.convert.dateOfBirth')}
                  required
                  error={fieldErrors.dateOfBirth}
                >
                  <input
                    type="date"
                    value={form.dateOfBirth}
                    disabled={submitting}
                    onChange={(e) => setField('dateOfBirth', e.target.value)}
                  />
                </FormField>

                <div className="fa-field">
                  <span className="fa-field__label">
                    {t('leads.convert.gender')}
                    <span className="fa-field__required" aria-hidden="true">
                      {' '}
                      *
                    </span>
                  </span>
                  <div
                    className="fa-radio-group"
                    role="radiogroup"
                    aria-label={t('leads.convert.gender')}
                  >
                    {genders.map((option) => (
                      <label key={option.value}>
                        <input
                          type="radio"
                          name="convert-gender"
                          value={option.value}
                          checked={form.gender === option.value}
                          disabled={submitting}
                          onChange={() => setField('gender', option.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {fieldErrors.gender && <p className="fa-field__error">{fieldErrors.gender}</p>}
                </div>
              </div>

              <FormField
                id="convert-nationalId"
                label={t('leads.convert.nationalId')}
                error={fieldErrors.nationalId}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  className="fa-ltr-isolate"
                  value={form.nationalId}
                  disabled={submitting}
                  onChange={(e) => setField('nationalId', e.target.value)}
                />
              </FormField>

              <div className="fa-field-row">
                <FormField
                  id="convert-gradeId"
                  label={t('leads.convert.grade')}
                  required
                  error={fieldErrors.gradeId}
                >
                  <LookupSelect
                    id="convert-gradeId"
                    value={form.gradeId}
                    onChange={(v) => setField('gradeId', v)}
                    items={grades}
                    placeholder={t('leads.convert.gradePlaceholder')}
                    disabled={submitting}
                  />
                </FormField>
                <FormField
                  id="convert-agreedSchoolId"
                  label={t('leads.convert.agreedSchool')}
                  required
                  error={fieldErrors.agreedSchoolId}
                >
                  <LookupSelect
                    id="convert-agreedSchoolId"
                    value={form.agreedSchoolId}
                    onChange={(v) => setField('agreedSchoolId', v)}
                    items={schools}
                    placeholder={t('leads.convert.agreedSchoolPlaceholder')}
                    disabled={submitting}
                  />
                </FormField>
                <FormField
                  id="convert-educationalAdministrationId"
                  label={t('leads.convert.educationalAdministration')}
                  required
                  error={fieldErrors.educationalAdministrationId}
                >
                  <LookupSelect
                    id="convert-educationalAdministrationId"
                    value={form.educationalAdministrationId}
                    onChange={(v) => setField('educationalAdministrationId', v)}
                    items={eduAdmins}
                    placeholder={t('leads.convert.educationalAdministrationPlaceholder')}
                    disabled={submitting}
                  />
                </FormField>
              </div>

              <FormField id="convert-address" label={t('leads.convert.address')}>
                <input
                  type="text"
                  value={form.address}
                  disabled={submitting}
                  onChange={(e) => setField('address', e.target.value)}
                />
              </FormField>

              <h3 className="fa-convert-dialog__section-title">
                {t('leads.convert.guardianSection')}
              </h3>

              <div className="fa-field-row">
                <FormField id="convert-guardianNameAr" label={t('leads.convert.guardianNameAr')}>
                  <input
                    type="text"
                    value={form.guardianNameAr}
                    disabled={submitting}
                    onChange={(e) => setField('guardianNameAr', e.target.value)}
                  />
                </FormField>
                <FormField id="convert-guardianNameEn" label={t('leads.convert.guardianNameEn')}>
                  <input
                    type="text"
                    value={form.guardianNameEn}
                    disabled={submitting}
                    onChange={(e) => setField('guardianNameEn', e.target.value)}
                  />
                </FormField>
              </div>

              <div className="fa-field-row">
                <FormField
                  id="convert-guardianRelationship"
                  label={t('leads.convert.guardianRelationship')}
                  required
                  error={fieldErrors.guardianRelationship}
                >
                  <select
                    value={form.guardianRelationship}
                    disabled={submitting}
                    onChange={(e) =>
                      setField('guardianRelationship', e.target.value as GuardianRelationship)
                    }
                  >
                    <option value="" disabled>
                      {t('leads.convert.guardianRelationshipPlaceholder')}
                    </option>
                    {relationships.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField
                  id="convert-guardianPhone"
                  label={t('leads.convert.guardianPhone')}
                  required
                  error={fieldErrors.guardianPhone}
                >
                  <input
                    type="tel"
                    className="fa-ltr-isolate"
                    value={form.guardianPhone}
                    disabled={submitting}
                    onChange={(e) => setField('guardianPhone', e.target.value)}
                  />
                </FormField>
              </div>

              <FormField id="convert-guardianEmail" label={t('leads.convert.guardianEmail')}>
                <input
                  type="email"
                  className="fa-ltr-isolate"
                  value={form.guardianEmail}
                  disabled={submitting}
                  onChange={(e) => setField('guardianEmail', e.target.value)}
                />
              </FormField>

              <h3 className="fa-convert-dialog__section-title">{t('leads.convert.planSection')}</h3>

              <div className="fa-field-row">
                <div className="fa-field">
                  <span className="fa-field__label" id="convert-installmentCount-label">
                    {t('leads.convert.installmentCount')}
                  </span>
                  <Stepper
                    id="convert-installmentCount"
                    value={form.installmentCount}
                    min={1}
                    onChange={(v) => setField('installmentCount', v)}
                    decrementLabel={t('table.pagination.previous')}
                    incrementLabel={t('table.pagination.next')}
                    disabled={submitting}
                  />
                  {fieldErrors.installmentCount && (
                    <p className="fa-field__error">{fieldErrors.installmentCount}</p>
                  )}
                </div>
                <FormField
                  id="convert-totalAgreedFees"
                  label={t('leads.convert.totalAgreedFees')}
                  error={fieldErrors.totalAgreedFees}
                >
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.totalAgreedFees}
                    disabled={submitting}
                    onChange={(e) =>
                      setField(
                        'totalAgreedFees',
                        e.target.value === '' ? '' : Number(e.target.value),
                      )
                    }
                  />
                </FormField>
              </div>
            </div>

            <div className="fa-dialog__actions">
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                disabled={submitting}
                onClick={onClose}
              >
                {t('leads.convert.cancel')}
              </button>
              <button type="submit" className="fa-btn fa-btn--primary" disabled={submitting}>
                {submitting ? t('leads.convert.submitting') : t('leads.convert.submit')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
